import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  X,
  User,
  UserCog,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Check,
  Phone,
  Mail,
  AlertTriangle,
  Briefcase,
  Keyboard,
  Info,
  Building2,
  Calendar,
  GraduationCap,
  CreditCard,
  DollarSign,
  Award,
  Search,
  FileText,
  Upload,
  Trash2,
  File,
  Users,
  ClipboardCheck,
} from 'lucide-react';
import { BulkImportProfessorsModal } from './BulkImportProfessorsModal';
import { BulkImportStaffModal } from './BulkImportStaffModal';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Permission } from '@/types/global.types';
import {
  createStaffUserWithDocuments,
  fetchActiveCampuses,
  fetchManagementPermissions,
  STAFF_DOCUMENT_TYPES,
  type CreateStaffUserDto,
  type CampusOption,
  type ManagementPermissions,
  type StaffDocumentType,
} from '@/services/userService';
import {
  fetchCountries,
  getCountriesSync,
  fetchDiplomes,
  fetchGrades,
  fetchDepartements,
  type NomenclatureCountry,
  type NomenclatureDiplome,
  type NomenclatureGrade,
  type NomenclatureDepartement,
} from '@/services/nomenclatureService';
import {
  fetchAcademicYears,
  fetchCurrentAcademicYear,
  generateAcademicYearOptions,
  getCurrentAcademicStartYear,
  generateAcademicYearName,
  type AcademicYear,
} from '@/services/academicPlanningService';
import { COUNTRY_PHONE_CODES, COUNTRY_PHONE_CODES_MAP, DEFAULT_PHONE_COUNTRY } from '@/shared/constants/phoneCodes';
import { emergencyRelationSelectOptions } from '@/shared/constants/emergencyRelation';

// ==================== CONSTANTS ====================

const ARABIC_KEYBOARD_LAYOUT = [
  ['ض', 'ص', 'ث', 'ق', 'ف', 'غ', 'ع', 'ه', 'خ', 'ح', 'ج'],
  ['ش', 'س', 'ي', 'ب', 'ل', 'ا', 'ت', 'ن', 'م', 'ك', 'ط'],
  ['ئ', 'ء', 'ؤ', 'ر', 'لا', 'ى', 'ة', 'و', 'ز', 'ظ', 'د'],
];

const HANDICAP_OPTIONS = [
  { value: 'N', label: 'Aucun handicap' },
  { value: 'A', label: 'Handicap auditif' },
  { value: 'M', label: 'Handicap moteur' },
  { value: 'V', label: 'Handicap visuel' },
  { value: 'AM', label: 'Auditif + Moteur' },
  { value: 'AV', label: 'Auditif + Visuel' },
  { value: 'MV', label: 'Moteur + Visuel' },
  { value: 'T', label: 'Handicap total' },
  { value: 'XX', label: 'Autre handicap' },
];

const FALLBACK_DIPLOMES = [
  { id: 'BAC', labelFr: 'Baccalauréat' },
  { id: 'DEUG', labelFr: 'DEUG' },
  { id: 'DEUST', labelFr: 'DEUST' },
  { id: 'DUT', labelFr: 'DUT' },
  { id: 'BTS', labelFr: 'BTS' },
  { id: 'LICENCE', labelFr: 'Licence' },
  { id: 'LP', labelFr: 'Licence Professionnelle' },
  { id: 'MASTER', labelFr: 'Master' },
  { id: 'INGENIEUR', labelFr: "Diplôme d'Ingénieur" },
  { id: 'DOCTORAT', labelFr: 'Doctorat' },
  { id: 'AUTRE', labelFr: 'Autre' },
];

// Staff roles that CAN ALSO be professors (they will see the "Êtes-vous aussi professeur?" question)
const STAFF_ROLES_WHO_CAN_TEACH = [
  'DIRECTOR_GENERAL',
  'BRANCH_MANAGER',
  'PEDAGOGICAL_DIRECTOR',
  'ACADEMIC_COORDINATOR',
  'FINANCIAL_MANAGER',
  'MARKETING_MANAGER',
  'COMMERCIAL_MANAGER',
  'PROGRAM_HEAD',
];

// Role hierarchy - defines which roles can be created by each role
// This MUST match the backend ROLE_HIERARCHY in role-hierarchy.config.ts
// Higher level roles can create lower level roles only
const ROLE_HIERARCHY: Record<string, string[]> = {
  'DIRECTOR_GENERAL': [
    'DIRECTOR_GENERAL', // DG can create another DG for succession
    'BRANCH_MANAGER',
    'PEDAGOGICAL_DIRECTOR',
    'FINANCIAL_MANAGER',
    'MARKETING_MANAGER',
    'COMMERCIAL_MANAGER',
    'PROGRAM_HEAD',
    'ACADEMIC_COORDINATOR',
    'TEACHER',
  ],
  'BRANCH_MANAGER': [
    // Branch Manager can create ALL staff roles EXCEPT DG
    'BRANCH_MANAGER',
    'PEDAGOGICAL_DIRECTOR',
    'FINANCIAL_MANAGER',
    'MARKETING_MANAGER',
    'COMMERCIAL_MANAGER',
    'PROGRAM_HEAD',
    'ACADEMIC_COORDINATOR',
    'TEACHER',
  ],
  'PEDAGOGICAL_DIRECTOR': [
    'PROGRAM_HEAD',
    'ACADEMIC_COORDINATOR',
    'TEACHER',
  ],
  'FINANCIAL_MANAGER': [
    // With STAFF_CREATE permission, can create all staff roles
    'BRANCH_MANAGER',
    'PEDAGOGICAL_DIRECTOR',
    'FINANCIAL_MANAGER',
    'MARKETING_MANAGER',
    'COMMERCIAL_MANAGER',
    'CALL_CENTER_AGENT',
    'PROGRAM_HEAD',
    'ACADEMIC_COORDINATOR',
    'TEACHER',
  ],
  'MARKETING_MANAGER': [
    'COMMERCIAL_MANAGER', // Can only create Commercial Managers (not professors)
  ],
  'PROGRAM_HEAD': [
    'ACADEMIC_COORDINATOR',
    'TEACHER',
  ],
  'ACADEMIC_COORDINATOR': [
    'TEACHER',
  ],
};

// ==================== HELPER FUNCTIONS ====================

// Capitalize first letter of each word (for first names)
const capitalizeFirstLetter = (value: string): string => {
  return value
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// ==================== TYPES ====================

interface CreateStaffModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// Section types matching ProfileForm structure
// Note: 'coordinates' and 'emergency' are kept for backwards compatibility, mapped to 'personal'
type Section = 'basic' | 'identity' | 'personal' | 'coordinates' | 'emergency' | 'professional' | 'alsoProf' | 'professorEdep' | 'campus' | 'documents' | 'physicalDocs';

// Document upload state
interface DocumentUpload {
  type: StaffDocumentType;
  file: File;
  preview?: string;
}

// ==================== SUB-COMPONENTS ====================

// Virtual Arabic Keyboard
const VirtualArabicKeyboard: React.FC<{
  onKeyPress: (char: string) => void;
  onBackspace: () => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLDivElement>;
}> = ({ onKeyPress, onBackspace, onClose, anchorRef }) => {
  const [position, setPosition] = useState({ top: 0, left: 0, width: 300 });

  useEffect(() => {
    const updatePosition = () => {
      if (anchorRef.current) {
        const rect = anchorRef.current.getBoundingClientRect();
        const keyboardHeight = 180;
        const viewportHeight = window.innerHeight;
        const showAbove = rect.bottom + keyboardHeight > viewportHeight && rect.top > keyboardHeight;

        setPosition({
          top: showAbove ? rect.top - keyboardHeight - 5 : rect.bottom + 5,
          left: rect.left,
          width: Math.max(rect.width, 280),
        });
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [anchorRef]);

  return ReactDOM.createPortal(
    <div
      data-arabic-keyboard
      className="fixed z-[9999] p-2 bg-white border border-gray-300 rounded-lg shadow-xl"
      style={{ top: position.top, left: position.left, width: position.width }}
    >
      <div className="flex justify-between items-center mb-2 pb-2 border-b border-gray-200">
        <span className="text-xs text-gray-500 font-medium">لوحة المفاتيح العربية</span>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="space-y-1">
        {ARABIC_KEYBOARD_LAYOUT.map((row, rowIndex) => (
          <div key={rowIndex} className="flex justify-center gap-0.5">
            {row.map((char, charIndex) => (
              <button
                key={charIndex}
                type="button"
                onClick={() => onKeyPress(char)}
                className="w-7 h-8 text-sm font-medium bg-gray-100 hover:bg-purple-100 hover:text-purple-700 rounded transition-colors border border-gray-200"
              >
                {char}
              </button>
            ))}
          </div>
        ))}
        <div className="flex justify-center gap-1 mt-2">
          <button
            type="button"
            onClick={() => onKeyPress(' ')}
            className="flex-1 h-8 text-xs bg-gray-100 hover:bg-gray-200 rounded border border-gray-200"
          >
            espace
          </button>
          <button
            type="button"
            onClick={onBackspace}
            className="w-16 h-8 text-xs bg-red-50 hover:bg-red-100 text-red-600 rounded border border-red-200"
          >
            ← effacer
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Arabic Input Component
const ArabicInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  error?: boolean;
}> = ({ value, onChange, placeholder, error }) => {
  const [showKeyboard, setShowKeyboard] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const keyboardElement = document.querySelector('[data-arabic-keyboard]');
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node) &&
        (!keyboardElement || !keyboardElement.contains(e.target as Node))
      ) {
        setShowKeyboard(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyPress = (char: string) => {
    const input = inputRef.current;
    if (input) {
      const start = input.selectionStart || value.length;
      const end = input.selectionEnd || value.length;
      const newValue = value.slice(0, start) + char + value.slice(end);
      onChange(newValue);
      setTimeout(() => {
        const newPos = start + char.length;
        input.setSelectionRange(newPos, newPos);
        input.focus();
      }, 0);
    } else {
      onChange(value + char);
    }
  };

  const handleBackspace = () => {
    const input = inputRef.current;
    if (input && value.length > 0) {
      const start = input.selectionStart || value.length;
      const end = input.selectionEnd || value.length;
      if (start === end && start > 0) {
        const newValue = value.slice(0, start - 1) + value.slice(end);
        onChange(newValue);
      } else if (start !== end) {
        const newValue = value.slice(0, start) + value.slice(end);
        onChange(newValue);
      }
    } else if (value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        dir="rtl"
        className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-right ${
          error ? 'border-red-300' : 'border-gray-300'
        }`}
        placeholder={placeholder}
      />
      <button
        type="button"
        onClick={() => {
          setShowKeyboard(!showKeyboard);
          if (!showKeyboard) inputRef.current?.focus();
        }}
        className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-colors ${
          showKeyboard ? 'bg-purple-100 text-purple-600' : 'text-gray-400 hover:text-gray-600'
        }`}
        title={showKeyboard ? 'Fermer le clavier' : 'Ouvrir le clavier arabe'}
      >
        <Keyboard className="w-4 h-4" />
      </button>

      {showKeyboard && (
        <VirtualArabicKeyboard
          onKeyPress={handleKeyPress}
          onBackspace={handleBackspace}
          onClose={() => setShowKeyboard(false)}
          anchorRef={containerRef}
        />
      )}
    </div>
  );
};

// Phone Country Selector
const PhoneCountrySelector: React.FC<{
  value: string;
  onChange: (code: string) => void;
}> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selected = COUNTRY_PHONE_CODES.find((c) => c.code === value) || COUNTRY_PHONE_CODES[0];

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 px-2 py-2 border border-gray-300 rounded-l-lg bg-gray-50 hover:bg-gray-100 min-w-[90px]"
      >
        <span className="text-lg">{selected.flag}</span>
        <span className="text-sm text-gray-600">{selected.dialCode}</span>
        <ChevronDown className="w-3 h-3 text-gray-400" />
      </button>

      {isOpen && (
        <div className="absolute z-50 top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {COUNTRY_PHONE_CODES.map((country) => (
            <button
              key={country.code}
              type="button"
              onClick={() => {
                onChange(country.code);
                setIsOpen(false);
              }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 ${
                value === country.code ? 'bg-purple-50' : ''
              }`}
            >
              <span className="text-lg">{country.flag}</span>
              <span className="flex-1 text-sm">{country.name}</span>
              <span className="text-xs text-gray-500">{country.dialCode}</span>
              {value === country.code && <Check className="w-4 h-4 text-purple-600" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Filterable Dropdown Component
interface FilterableDropdownOption {
  value: string;
  label: string;
  sublabel?: string;
}

const FilterableDropdown: React.FC<{
  value: string;
  onChange: (value: string) => void;
  options: FilterableDropdownOption[];
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
}> = ({ value, onChange, options, placeholder = 'Sélectionner...', disabled = false, error = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);

  const filtered =
    isTyping && inputValue
      ? options.filter(
          (o) =>
            o.label.toLowerCase().includes(inputValue.toLowerCase()) ||
            (o.sublabel && o.sublabel.toLowerCase().includes(inputValue.toLowerCase()))
        )
      : options;

  useEffect(() => {
    if (!isOpen || !containerRef.current) return;

    const updatePosition = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width,
        });
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const isOutsideContainer = containerRef.current && !containerRef.current.contains(target);
      const isOutsideDropdown = !dropdownRef.current || !dropdownRef.current.contains(target);

      if (isOutsideContainer && isOutsideDropdown) {
        setIsOpen(false);
        setInputValue('');
        setIsTyping(false);
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setIsTyping(true);
    if (!isOpen) setIsOpen(true);
  };

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setInputValue('');
    setIsTyping(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setInputValue('');
      setIsTyping(false);
    }
    if (e.key === 'Enter' && filtered.length === 1) {
      handleSelect(filtered[0].value);
    }
  };

  const displayValue = isTyping ? inputValue : selected?.label || '';

  const dropdownMenu = isOpen
    ? ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className="fixed bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto"
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            zIndex: 9999,
          }}
        >
          {filtered.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">Aucun résultat</div>
          ) : (
            filtered.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                className={`w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-gray-50 transition-colors ${
                  value === option.value ? 'bg-purple-50' : ''
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-900 truncate">{option.label}</div>
                  {option.sublabel && <div className="text-xs text-gray-500 truncate">{option.sublabel}</div>}
                </div>
                {value === option.value && <Check className="w-4 h-4 text-purple-600 flex-shrink-0 ml-2" />}
              </button>
            ))
          )}
        </div>,
        document.body
      )
    : null;

  return (
    <div className="relative" ref={containerRef}>
      <div
        className={`w-full flex items-center border rounded-lg transition-colors ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
        } ${error ? 'border-red-300' : 'border-gray-300'} ${isOpen ? 'ring-2 ring-purple-500 border-purple-500' : ''}`}
      >
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full px-4 py-2 bg-transparent rounded-lg focus:outline-none ${
            disabled ? 'cursor-not-allowed text-gray-500' : 'text-gray-900'
          } ${!displayValue ? 'text-gray-500' : ''}`}
        />
        <button type="button" onClick={() => !disabled && setIsOpen(!isOpen)} disabled={disabled} className="px-2 py-2">
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {dropdownMenu}
    </div>
  );
};

// Section Header Component
const SectionHeader: React.FC<{
  title: string;
  icon: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  required?: boolean;
  optional?: boolean;
}> = ({ title, icon, expanded, onToggle, required, optional }) => (
  <button
    type="button"
    onClick={onToggle}
    className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
  >
    <div className="flex items-center gap-2">
      {icon}
      <span className="font-medium text-gray-900">{title}</span>
      {required && <span className="text-xs text-red-500">*Obligatoire</span>}
      {optional && <span className="text-xs text-gray-500">(Optionnel)</span>}
    </div>
    {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
  </button>
);

// ==================== MAIN COMPONENT ====================

export const CreateStaffModal: React.FC<CreateStaffModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<Section>>(
    new Set(['basic', 'identity', 'personal', 'professional', 'campus', 'documents', 'physicalDocs'])
  );

  const { hasPermission } = usePermissions();
  const canBulkCreateStaff = hasPermission(Permission.STAFF_BULK_CREATE);
  const canBulkCreateTeacher = hasPermission(Permission.STAFF_BULK_CREATE_TEACHER);

  // Permissions and nomenclature data
  const [permissions, setPermissions] = useState<ManagementPermissions | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<string>('');
  const [currentUserCampusIds, setCurrentUserCampusIds] = useState<string[]>([]);
  const [countries, setCountries] = useState<NomenclatureCountry[]>(() => getCountriesSync());
  const [diplomes, setDiplomes] = useState<NomenclatureDiplome[]>([]);
  const [grades, setGrades] = useState<NomenclatureGrade[]>([]);
  const [departements, setDepartements] = useState<NomenclatureDepartement[]>([]);

  // Campus data
  const [campuses, setCampuses] = useState<CampusOption[]>([]);
  const [requiresCampusSelection, setRequiresCampusSelection] = useState(false);
  const [selectedCampusIds, setSelectedCampusIds] = useState<string[]>([]);
  const [payrollCampusId, setPayrollCampusId] = useState<string>('');

  // Academic year data - includes both existing years from DB and generated years
  // Generated years have id as the year name (e.g., "2024/2025")
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]);
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState<string>('');

  // Form state - Required fields
  const [selectedRole, setSelectedRole] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');

  // Contact fields
  const [phone, setPhone] = useState('');
  const [phoneCountry, setPhoneCountry] = useState(DEFAULT_PHONE_COUNTRY);
  const [adresse, setAdresse] = useState('');

  // Identity fields
  const [prenomArabe, setPrenomArabe] = useState('');
  const [nomArabe, setNomArabe] = useState('');
  const [sexe, setSexe] = useState<'M' | 'F' | ''>('');
  const [dateNaissance, setDateNaissance] = useState('');
  const [paysOrigine, setPaysOrigine] = useState('MA');
  const [cnie, setCnie] = useState('');
  const [passport, setPassport] = useState('');
  const [handicap, setHandicap] = useState('N');

  // Emergency contact
  const [urgenceNom, setUrgenceNom] = useState('');
  const [urgenceNumero, setUrgenceNumero] = useState('');
  const [urgenceAdresse, setUrgenceAdresse] = useState('');
  const [urgencePhoneCountry, setUrgencePhoneCountry] = useState('MA');
  const [urgenceRelation, setUrgenceRelation] = useState('');

  // Professional fields (for teachers)
  const [dateRecrutement, setDateRecrutement] = useState('');
  const [fonction, setFonction] = useState('');
  const [diplomeId, setDiplomeId] = useState('');
  const [specialite, setSpecialite] = useState('');
  const [matieresEnseignees, setMatieresEnseignees] = useState('');
  const [statutProfesseur, setStatutProfesseur] = useState('');
  const [tauxHoraire, setTauxHoraire] = useState('');

  // E-DEP Professor fields
  const [gradeId, setGradeId] = useState('');
  const [departementAttacheId, setDepartementAttacheId] = useState('');
  const [estOriginaireEtablissementPublic, setEstOriginaireEtablissementPublic] = useState('');
  const [etablissementOrigine, setEtablissementOrigine] = useState('');
  const [rib, setRib] = useState('');
  const [salaireMensuelNet, setSalaireMensuelNet] = useState('');

  // Dropdown search states
  const [gradeSearch, setGradeSearch] = useState('');
  const [showGradeDropdown, setShowGradeDropdown] = useState(false);
  const [deptSearch, setDeptSearch] = useState('');
  const [showDeptDropdown, setShowDeptDropdown] = useState(false);
  const [diplomeSearch, setDiplomeSearch] = useState('');
  const [showDiplomeDropdown, setShowDiplomeDropdown] = useState(false);
  const gradeDropdownRef = useRef<HTMLDivElement>(null);
  const deptDropdownRef = useRef<HTMLDivElement>(null);
  const diplomeDropdownRef = useRef<HTMLDivElement>(null);
  const diplomeInputRef = useRef<HTMLInputElement>(null);
  const gradeInputRef = useRef<HTMLInputElement>(null);
  const deptInputRef = useRef<HTMLInputElement>(null);

  // Dropdown positions for portal rendering
  const [diplomeDropdownPos, setDiplomeDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const [gradeDropdownPos, setGradeDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const [deptDropdownPos, setDeptDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  // "Also professor" state for staff who can teach
  const [isAlsoProfessor, setIsAlsoProfessor] = useState<boolean | null>(null);

  // Document uploads state
  const [documents, setDocuments] = useState<DocumentUpload[]>([]);

  // Physical document tracking state
  const [physicalDocCin, setPhysicalDocCin] = useState(false);
  const [physicalDocPassport, setPhysicalDocPassport] = useState(false);
  const [physicalDocCv, setPhysicalDocCv] = useState(false);
  const [physicalDocContratTravail, setPhysicalDocContratTravail] = useState(false);
  const [physicalDocDiplome, setPhysicalDocDiplome] = useState(false);
  const [physicalDocAttestationRib, setPhysicalDocAttestationRib] = useState(false);
  const [physicalDocEngagementProfesseur, setPhysicalDocEngagementProfesseur] = useState(false);
  const [physicalDocReglementInterieur, setPhysicalDocReglementInterieur] = useState(false);
  const [physicalDocConsentement, setPhysicalDocConsentement] = useState(false);

  // Bulk import modal state
  const [showBulkImportProfessorsModal, setShowBulkImportProfessorsModal] = useState(false);
  const [showBulkImportStaffModal, setShowBulkImportStaffModal] = useState(false);

  // Filter staff roles from creatable roles based on role hierarchy
  const creatableStaffRoles = React.useMemo(() => {
    if (!permissions?.creatableRoles) return [];

    // Filter out student and parent roles
    let staffRoles = permissions.creatableRoles.filter((r) => !['STUDENT', 'PARENT'].includes(r.role));

    // Apply role hierarchy restriction
    const allowedRoles = ROLE_HIERARCHY[currentUserRole];
    if (allowedRoles) {
      staffRoles = staffRoles.filter((r) => allowedRoles.includes(r.role));
    }

    return staffRoles;
  }, [permissions?.creatableRoles, currentUserRole]);

  // Check if selected role is TEACHER
  const isTeacherRole = selectedRole === 'TEACHER';

  // Check if role can also be a professor (staff who can teach)
  const canAlsoBeProfesor = STAFF_ROLES_WHO_CAN_TEACH.includes(selectedRole);

  // Show professor fields if user is TEACHER OR if they're a staff member who also teaches
  const showProfessorFields = isTeacherRole || (canAlsoBeProfesor && isAlsoProfessor === true);

  // Check if vacataire
  const isVacataire = statutProfesseur === 'V';

  // Check if role can be multi-campus
  const isMultiCampusRole = ['BRANCH_MANAGER', 'PEDAGOGICAL_DIRECTOR', 'FINANCIAL_MANAGER', 'MARKETING_MANAGER', 'COMMERCIAL_MANAGER', 'PROGRAM_HEAD', 'TEACHER'].includes(selectedRole);

  // Check if current user is a branch manager (they can only assign to their campus)
  const isBranchManager = currentUserRole === 'BRANCH_MANAGER';

  // Filter available campuses for branch managers (only their assigned campuses)
  const availableCampuses = React.useMemo(() => {
    if (isBranchManager && currentUserCampusIds.length > 0) {
      return campuses.filter((c) => currentUserCampusIds.includes(c.id));
    }
    return campuses;
  }, [campuses, isBranchManager, currentUserCampusIds]);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      // Check if click is inside portal dropdowns
      const portalDropdown = document.querySelector('[data-dropdown-portal]');
      if (portalDropdown && portalDropdown.contains(e.target as Node)) {
        return;
      }
      if (gradeDropdownRef.current && !gradeDropdownRef.current.contains(e.target as Node)) {
        setShowGradeDropdown(false);
      }
      if (deptDropdownRef.current && !deptDropdownRef.current.contains(e.target as Node)) {
        setShowDeptDropdown(false);
      }
      if (diplomeDropdownRef.current && !diplomeDropdownRef.current.contains(e.target as Node)) {
        setShowDiplomeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate dropdown position for portal rendering
  const updateDropdownPosition = (inputRef: React.RefObject<HTMLInputElement | null>, setPos: React.Dispatch<React.SetStateAction<{ top: number; left: number; width: number }>>) => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  };

  const loadData = async () => {
    // Clear any previous errors when loading fresh data
    setError(null);
    setSuccess(false);

    try {
      // Get auth token for departements API and academic years
      const token = localStorage.getItem('nexedu_access_token') || '';

      const [permissionsData, countriesData, diplomesData, campusesData, gradesData, departementsData, academicYearsData, currentAcademicYear] = await Promise.all([
        fetchManagementPermissions(),
        fetchCountries().catch(() => []),
        fetchDiplomes().catch(() => []),
        fetchActiveCampuses().catch(() => ({ campuses: [], count: 0, requiresCampusSelection: false })),
        fetchGrades().catch(() => []),
        fetchDepartements(token).catch(() => []),
        fetchAcademicYears(token).catch(() => []),
        fetchCurrentAcademicYear(token).catch(() => null),
      ]);

      setPermissions(permissionsData);
      setCurrentUserRole(permissionsData.currentRole || '');
      setCurrentUserCampusIds(permissionsData.currentUserCampusIds || []);
      if (countriesData?.length) setCountries(countriesData);
      if (diplomesData?.length) setDiplomes(diplomesData);
      if (gradesData?.length) setGrades(gradesData);
      if (departementsData?.length) setDepartements(departementsData);

      // Generate academic year options: 2 years before and 2 years after current year
      const generatedYearOptions = generateAcademicYearOptions(2, 2);
      const currentYearName = generateAcademicYearName(getCurrentAcademicStartYear());

      // Create combined list of academic years
      // Use DB ID if available, otherwise use the year name as ID (for new years)
      const combinedYears: AcademicYear[] = generatedYearOptions.map(option => {
        const existingYear = academicYearsData?.find((y: AcademicYear) => y.name === option.value);
        if (existingYear) {
          return existingYear;
        }
        // Create a virtual year entry for years not in DB
        return {
          id: option.value, // Use name as ID for virtual years
          name: option.value,
          isCurrent: option.value === currentYearName,
          isActive: true,
          startDate: '',
          endDate: '',
          tenantId: '',
          createdAt: '',
          updatedAt: '',
        };
      });

      setAcademicYears(combinedYears);

      // Set default to current academic year (always based on today's date)
      // First priority: find the computed current year in combined list
      const currentYear = combinedYears.find(y => y.name === currentYearName);
      if (currentYear) {
        setSelectedAcademicYearId(currentYear.id);
      } else if (currentAcademicYear?.id) {
        // Fallback to backend's current academic year
        setSelectedAcademicYearId(currentAcademicYear.id);
      } else if (combinedYears.length > 0) {
        // Last fallback: first year in list
        setSelectedAcademicYearId(combinedYears[0].id);
      }

      // Set campus data
      if (campusesData?.campuses) {
        setCampuses(campusesData.campuses);
        setRequiresCampusSelection(campusesData.requiresCampusSelection);
        // If only one campus, auto-select it for both campus assignment and payroll
        if (campusesData.campuses.length === 1) {
          setSelectedCampusIds([campusesData.campuses[0].id]);
          setPayrollCampusId(campusesData.campuses[0].id);
        }
        // For BRANCH_MANAGER: auto-select their assigned campus(es)
        else if (permissionsData.currentRole === 'BRANCH_MANAGER' && permissionsData.currentUserCampusIds && permissionsData.currentUserCampusIds.length > 0) {
          setSelectedCampusIds([...permissionsData.currentUserCampusIds]);
        }
      }

      // Set default role if available
      const staffRoles = permissionsData.creatableRoles.filter(
        (r) => !['STUDENT', 'PARENT'].includes(r.role)
      );
      if (staffRoles.length > 0) {
        const defaultRole = staffRoles[0].role;
        setSelectedRole(defaultRole);
        // Set default status to Permanent for TEACHER role
        if (defaultRole === 'TEACHER') {
          setStatutProfesseur('P');
        }
      }
    } catch (err) {
      console.error('Error loading data:', err);
    }
  };

  const toggleSection = (section: Section) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  // Toggle campus selection
  const toggleCampusSelection = (campusId: string) => {
    if (isMultiCampusRole) {
      // Multi-campus: toggle selection
      setSelectedCampusIds((prev) =>
        prev.includes(campusId) ? prev.filter((id) => id !== campusId) : [...prev, campusId]
      );
      // Don't auto-set payrollCampusId - user must always choose when multiple campuses exist
    } else {
      // Single-campus role: replace selection
      setSelectedCampusIds([campusId]);
      // Don't auto-set payrollCampusId - user must always choose when multiple campuses exist
    }
  };

  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setPhoneCountry('MA');
    setAdresse('');
    setPrenomArabe('');
    setNomArabe('');
    setSexe('');
    setDateNaissance('');
    setPaysOrigine('MA');
    setCnie('');
    setPassport('');
    setHandicap('N');
    setUrgenceNom('');
    setUrgenceNumero('');
    setUrgenceAdresse('');
    setUrgencePhoneCountry('MA');
    setUrgenceRelation('');
    setDateRecrutement('');
    setFonction('');
    setDiplomeId('');
    setSpecialite('');
    setMatieresEnseignees('');
    setStatutProfesseur('');
    setTauxHoraire('');
    // E-DEP Professor fields
    setGradeId('');
    setDepartementAttacheId('');
    setEstOriginaireEtablissementPublic('');
    setEtablissementOrigine('');
    setRib('');
    setSalaireMensuelNet('');
    // Dropdown states
    setGradeSearch('');
    setShowGradeDropdown(false);
    setDeptSearch('');
    setShowDeptDropdown(false);
    setDiplomeSearch('');
    setShowDiplomeDropdown(false);
    // Also professor state
    setIsAlsoProfessor(null);
    setSelectedCampusIds(campuses.length === 1 ? [campuses[0].id] : []);
    // Payroll campus - auto-select if only one campus
    setPayrollCampusId(campuses.length === 1 ? campuses[0].id : '');
    // Reset academic year to current (if available)
    const currentYear = academicYears.find((y) => y.isCurrent);
    setSelectedAcademicYearId(currentYear?.id || (academicYears.length > 0 ? academicYears[0].id : ''));
    // Documents state - cleanup preview URLs
    documents.forEach(doc => {
      if (doc.preview) URL.revokeObjectURL(doc.preview);
    });
    setDocuments([]);
    // Physical document tracking
    setPhysicalDocCin(false);
    setPhysicalDocPassport(false);
    setPhysicalDocCv(false);
    setPhysicalDocContratTravail(false);
    setPhysicalDocDiplome(false);
    setPhysicalDocAttestationRib(false);
    setPhysicalDocEngagementProfesseur(false);
    setError(null);
    setSuccess(false);
    setExpandedSections(new Set(['basic', 'identity', 'personal', 'professional', 'campus', 'documents', 'physicalDocs']));
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Helper function to scroll to field and expand section
  const scrollToField = (fieldId: string, sectionToExpand?: Section) => {
    // Expand the section first if needed
    if (sectionToExpand && !expandedSections.has(sectionToExpand)) {
      setExpandedSections(prev => new Set([...prev, sectionToExpand]));
    }

    // Wait for DOM update then scroll
    setTimeout(() => {
      const element = document.getElementById(fieldId) || document.querySelector(`[name="${fieldId}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Focus the element if it's an input
        if (element instanceof HTMLInputElement || element instanceof HTMLSelectElement || element instanceof HTMLTextAreaElement) {
          element.focus();
        }
        // Add highlight effect
        element.classList.add('ring-2', 'ring-red-500', 'ring-offset-2');
        setTimeout(() => {
          element.classList.remove('ring-2', 'ring-red-500', 'ring-offset-2');
        }, 3000);
      }
    }, 100);
  };

  // Validation error with field info
  const throwValidationError = (message: string, fieldId: string, section?: Section) => {
    scrollToField(fieldId, section);
    throw new Error(message);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validate basic required fields
      if (!firstName) {
        throwValidationError('Le prénom est obligatoire', 'staff-firstName', 'basic');
      }
      if (!lastName) {
        throwValidationError('Le nom est obligatoire', 'staff-lastName', 'basic');
      }
      if (!email) {
        throwValidationError("L'email est obligatoire", 'staff-email', 'basic');
      }
      if (!selectedRole) {
        throwValidationError('Le rôle est obligatoire', 'staff-role', 'basic');
      }

      // Validate email format
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        throwValidationError("Format d'email invalide", 'staff-email', 'basic');
      }

      // Skip campus validation for DG (they get all campuses automatically)
      const isGlobalRole = selectedRole === 'DIRECTOR_GENERAL';

      // Validate campus selection if required (not for global roles like DG)
      // For single campus, it's auto-selected so this should always pass
      if (!isGlobalRole && selectedCampusIds.length === 0) {
        throwValidationError("Veuillez sélectionner au moins un campus d'affectation", 'staff-campus', 'campus');
      }

      // Validate payroll campus (required for all non-DG roles)
      // For single campus, it's auto-selected so this should always pass
      if (!isGlobalRole && !payrollCampusId) {
        throwValidationError("Veuillez sélectionner le campus de paie", 'staff-payrollCampus', 'campus');
      }

      // Validate phone (required)
      if (!phone) {
        throwValidationError('Le numéro de téléphone est obligatoire', 'staff-phone', 'coordinates');
      }

      // Validate phone format
      // Since the country code is selected separately, we validate just the number part
      if (phone) {
        // Remove any leading zeros or spaces from the phone number
        const cleanedPhone = phone.replace(/^0+/, '').replace(/\s/g, '');

        if (phoneCountry === DEFAULT_PHONE_COUNTRY) {
          // Moroccan format: 9 digits starting with 5, 6, or 7 (without the +212 prefix)
          if (!/^[5-7]\d{8}$/.test(cleanedPhone)) {
            throwValidationError('Le numéro doit contenir 9 chiffres commençant par 5, 6 ou 7 (ex: 612345678)', 'staff-phone', 'coordinates');
          }
        } else {
          // International format: must have at least 7 digits
          const digitsOnly = cleanedPhone.replace(/\D/g, '');
          if (digitsOnly.length < 7 || digitsOnly.length > 15) {
            throwValidationError('Le numéro de téléphone doit contenir entre 7 et 15 chiffres', 'staff-phone', 'coordinates');
          }
        }
      }

      // Validate emergency phone format if provided
      if (urgenceNumero) {
        // Remove any leading zeros or spaces from the phone number
        const cleanedUrgencePhone = urgenceNumero.replace(/^0+/, '').replace(/\s/g, '');

        if (urgencePhoneCountry === 'MA') {
          // Moroccan format: 9 digits starting with 5, 6, or 7 (without the +212 prefix)
          if (!/^[5-7]\d{8}$/.test(cleanedUrgencePhone)) {
            throwValidationError("Le numéro d'urgence doit contenir 9 chiffres commençant par 5, 6 ou 7 (ex: 612345678)", 'staff-urgenceNumero', 'emergency');
          }
        } else {
          // International format: must have at least 7 digits
          const digitsOnly = cleanedUrgencePhone.replace(/\D/g, '');
          if (digitsOnly.length < 7 || digitsOnly.length > 15) {
            throwValidationError("Le numéro d'urgence doit contenir entre 7 et 15 chiffres", 'staff-urgenceNumero', 'emergency');
          }
        }
      }

      // Validate professional info fields (required: fonction, dateRecrutement, salaireMensuelNet)
      if (!fonction) {
        throwValidationError('La fonction est obligatoire', 'staff-fonction', 'professional');
      }
      if (!dateRecrutement) {
        throwValidationError('La date de recrutement est obligatoire', 'staff-dateRecrutement', 'professional');
      }
      // Salary validation:
      // - TEACHER Permanent: requires salaireMensuelNet
      // - TEACHER Vacataire: requires tauxHoraire
      // - Other staff (non-vacataire): requires salaireMensuelNet
      if (isTeacherRole && statutProfesseur === 'V') {
        if (!tauxHoraire) {
          throwValidationError('Le taux horaire est obligatoire pour les professeurs vacataires', 'staff-tauxHoraire', 'professional');
        }
      } else if (!isVacataire && !salaireMensuelNet) {
        throwValidationError('Le salaire mensuel net est obligatoire', 'staff-salaireMensuelNet', 'professional');
      }

      // Validate RIB format if provided (24 digits)
      if (rib) {
        const cleanRib = rib.replace(/\s/g, '');
        if (cleanRib.length !== 24 || !/^\d{24}$/.test(cleanRib)) {
          throwValidationError('Le RIB doit contenir exactement 24 chiffres', 'staff-rib', 'professional');
        }
      }

      // Validate isAlsoProfessor for staff roles that can also teach (required)
      if (canAlsoBeProfesor && isAlsoProfessor === null) {
        throwValidationError("Veuillez indiquer si vous avez une activité d'enseignement", 'staff-isAlsoProfessor', 'alsoProf');
      }

      // Validate département d'attache when teaching activity is yes
      if (showProfessorFields && !departementAttacheId) {
        throwValidationError("Le département d'attache est obligatoire pour les enseignants", 'staff-departementAttacheId', 'professorEdep');
      }

      // Build the DTO
      // Morocco: send the bare 9-digit national number (country code is sent separately).
      // Other countries: send E.164 format (+<dialcode><national>) - the backend phone
      // regex only accepts international numbers with a leading '+'.
      const nationalPhone = phone ? phone.replace(/^0+/, '').replace(/\D/g, '') : ''; // Remove leading zeros and non-digits
      const cleanPhone =
        nationalPhone && phoneCountry !== DEFAULT_PHONE_COUNTRY
          ? `${COUNTRY_PHONE_CODES_MAP[phoneCountry]?.dialCode || ''}${nationalPhone}`
          : nationalPhone;
      const cleanUrgencePhone = urgenceNumero ? urgenceNumero.replace(/^0+/, '') : ''; // Remove leading zeros

      const data: CreateStaffUserDto = {
        email,
        firstName,
        lastName,
        role: selectedRole,
        // Contact fields - send phone number without dial code prefix, country code is sent separately
        ...(cleanPhone && { phone: cleanPhone }),
        ...(phoneCountry && { phoneCountry }),
        ...(adresse && { adresse }),
        // Identity fields
        ...(prenomArabe && { prenomArabe }),
        ...(nomArabe && { nomArabe }),
        ...(cnie && { cnie: cnie.toUpperCase() }),
        ...(passport && { passport }),
        ...(sexe && { sexe: sexe as 'M' | 'F' }),
        ...(dateNaissance && { dateNaissance }),
        ...(paysOrigine && { paysOrigine }),
        ...(handicap && { handicap }),
        // Emergency contact - send phone number without dial code prefix, country code is sent separately
        ...(urgenceNom && { urgenceNom }),
        ...(cleanUrgencePhone && { urgenceNumero: cleanUrgencePhone }),
        ...(urgenceAdresse && { urgenceAdresse }),
        ...(urgenceRelation && { urgenceRelation }),
        ...(urgencePhoneCountry && { urgencePhoneCountry }),
        // Professional fields (for teachers)
        ...(dateRecrutement && { dateRecrutement }),
        ...(fonction && { fonction }),
        ...(diplomeId && { diplomeId }),
        ...(specialite && { specialite }),
        ...(matieresEnseignees && { matieresEnseignees }),
        ...(statutProfesseur && { statutProfesseur }),
        ...(tauxHoraire && { tauxHoraire }),
        // E-DEP Professor fields
        ...(gradeId && { gradeId }),
        ...(departementAttacheId && { departementAttacheId }),
        ...(estOriginaireEtablissementPublic && { estOriginaireEtablissementPublic }),
        ...(etablissementOrigine && { etablissementOrigine }),
        ...(rib && { rib: rib.replace(/\s/g, '') }), // Remove spaces from RIB
        // Remove all formatting (spaces, non-breaking spaces, dots, commas) from salary to store as plain number string
        ...(salaireMensuelNet && { salaireMensuelNet: salaireMensuelNet.replace(/[\s\u00A0.,]/g, '') }),
        // isAlsoProfessor flag for staff who can also teach
        ...(isAlsoProfessor !== null && { isAlsoProfessor }),
        // Campus assignment
        ...(selectedCampusIds.length > 0 && { campusIds: selectedCampusIds }),
        // Payroll campus
        ...(payrollCampusId && { payrollCampusId }),
        // Academic year assignment
        ...(selectedAcademicYearId && { academicYearId: selectedAcademicYearId }),
        // Physical document tracking
        physicalDocCin,
        physicalDocPassport,
        physicalDocCv,
        physicalDocContratTravail,
        physicalDocDiplome,
        physicalDocAttestationRib,
        physicalDocEngagementProfesseur,
        physicalDocReglementInterieur,
        physicalDocConsentement,
      };

      // Prepare documents for upload
      const documentsToUpload = documents.map(doc => ({
        type: doc.type,
        file: doc.file,
      }));

      const result = await createStaffUserWithDocuments(data, documentsToUpload);

      // Check if any document uploads failed
      const failedUploads = result.uploadedDocuments.filter(d => !d.success);
      if (failedUploads.length > 0) {
        console.warn('Some documents failed to upload:', failedUploads);
        // Still show success but with warning about documents
        setError(`Compte créé mais ${failedUploads.length} document(s) n'ont pas pu être téléchargés.`);
      }

      setSuccess(true);

      // Delay to show success message
      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  // Document handling functions
  const handleDocumentAdd = (type: StaffDocumentType, file: File) => {
    // Check if document type already exists
    const existingIndex = documents.findIndex(d => d.type === type);

    // Create preview URL for images
    const isImage = file.type.startsWith('image/');
    const preview = isImage ? URL.createObjectURL(file) : undefined;

    if (existingIndex >= 0) {
      // Replace existing document
      const newDocs = [...documents];
      if (newDocs[existingIndex].preview) {
        URL.revokeObjectURL(newDocs[existingIndex].preview!);
      }
      newDocs[existingIndex] = { type, file, preview };
      setDocuments(newDocs);
    } else {
      // Add new document
      setDocuments([...documents, { type, file, preview }]);
    }
  };

  const handleDocumentRemove = (type: StaffDocumentType) => {
    const doc = documents.find(d => d.type === type);
    if (doc?.preview) {
      URL.revokeObjectURL(doc.preview);
    }
    setDocuments(documents.filter(d => d.type !== type));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Get diplomes list
  const diplomesList =
    diplomes.length > 0
      ? diplomes.map((d) => ({ id: d.id, label: d.labelFr }))
      : FALLBACK_DIPLOMES.map((d) => ({ id: d.id, label: d.labelFr }));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <UserCog className="text-purple-600" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Ajouter un membre du personnel</h2>
              <p className="text-sm text-gray-500">Créer un nouveau compte avec toutes les informations</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Import en masse dropdown - only shown if user has at least one bulk create permission */}
            {(canBulkCreateStaff || canBulkCreateTeacher) && (
              <div className="relative group">
                <button
                  type="button"
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
                >
                  <Upload size={16} />
                  Import en masse
                  <ChevronDown size={14} />
                </button>
                <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                  {canBulkCreateTeacher && (
                    <button
                      type="button"
                      onClick={() => setShowBulkImportProfessorsModal(true)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-purple-50 transition-colors rounded-t-lg"
                    >
                      <GraduationCap size={18} className="text-purple-600" />
                      <div className="text-left">
                        <div className="font-medium">Import enseignants en masse</div>
                        <div className="text-xs text-gray-500">Importer depuis un fichier Excel</div>
                      </div>
                    </button>
                  )}
                  {canBulkCreateStaff && (
                    <button
                      type="button"
                      onClick={() => setShowBulkImportStaffModal(true)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-purple-50 transition-colors rounded-b-lg ${canBulkCreateTeacher ? 'border-t border-gray-100' : ''}`}
                    >
                      <Users size={18} className="text-purple-600" />
                      <div className="text-left">
                        <div className="font-medium">Import personnel en masse</div>
                        <div className="text-xs text-gray-500">Importer depuis un fichier Excel</div>
                      </div>
                    </button>
                  )}
                </div>
              </div>
            )}
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Success Message */}
          {success && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
              <Check className="text-green-600" size={20} />
              <span className="text-green-800">Membre du personnel créé avec succès ! Un email de vérification a été envoyé.</span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
              <AlertCircle className="text-red-600" size={20} />
              <span className="text-red-800">{error}</span>
            </div>
          )}

          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium">Création de compte personnel</p>
              <p>
                Les champs obligatoires sont marqués par une étoile rouge (*). Le membre pourra compléter son profil après
                activation de son compte.
              </p>
            </div>
          </div>

          {/* Role Selection */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rôle <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedRole}
              onChange={(e) => {
                const newRole = e.target.value;
                setSelectedRole(newRole);
                // Reset campus selection when role changes
                if (campuses.length === 1) {
                  setSelectedCampusIds([campuses[0].id]);
                } else {
                  setSelectedCampusIds([]);
                }
                // Set default status to Permanent for TEACHER role
                if (newRole === 'TEACHER') {
                  setStatutProfesseur('P');
                } else {
                  setStatutProfesseur('');
                }
                // Reset isAlsoProfessor when role changes
                setIsAlsoProfessor(null);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              required
            >
              {creatableStaffRoles.map((role) => (
                <option key={role.role} value={role.role}>
                  {role.nameFr}
                </option>
              ))}
            </select>
          </div>

          {/* Academic Year Selection */}
          {academicYears.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-purple-600" />
                  Année académique
                </div>
              </label>
              <select
                value={selectedAcademicYearId}
                onChange={(e) => setSelectedAcademicYearId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                {academicYears.map((year) => (
                  <option key={year.id} value={year.id}>
                    {year.name} {year.isCurrent ? '(Année en cours)' : ''}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                L&apos;année académique sera automatiquement mise à jour pour les membres actifs.
              </p>
            </div>
          )}

          {/* Section 1: Identité */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <SectionHeader
              title="Section 1: Identité"
              icon={<User size={18} className="text-purple-600" />}
              expanded={expandedSections.has('basic')}
              onToggle={() => toggleSection('basic')}
              required
            />

            {expandedSections.has('basic') && (
              <div className="p-6 space-y-4">
                {/* Nom / Prénom */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="staff-lastName"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value.toUpperCase())}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 uppercase"
                      placeholder="NOM"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prénom <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="staff-firstName"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(capitalizeFirstLetter(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="Prénom"
                      required
                    />
                  </div>
                </div>

                {/* Nom / Prénom Arabe */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      الاسم العائلي / Nom en arabe
                    </label>
                    <ArabicInput value={nomArabe} onChange={setNomArabe} placeholder="الاسم العائلي" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      الاسم الشخصي / Prénom en arabe
                    </label>
                    <ArabicInput value={prenomArabe} onChange={setPrenomArabe} placeholder="الاسم الشخصي" />
                  </div>
                </div>

                {/* CNIE / Passport */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">N° CNIE</label>
                    <input
                      type="text"
                      value={cnie}
                      onChange={(e) => setCnie(e.target.value.toUpperCase())}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="AB123456"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">N° Passeport</label>
                    <input
                      type="text"
                      value={passport}
                      onChange={(e) => setPassport(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="Numéro passeport"
                    />
                  </div>
                </div>

                {/* Sexe / Date de naissance */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sexe</label>
                    <div className="flex gap-4">
                      <label
                        className={`flex-1 flex items-center justify-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                          sexe === 'M'
                            ? 'border-purple-500 bg-purple-50 text-purple-700'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <input
                          type="radio"
                          value="M"
                          checked={sexe === 'M'}
                          onChange={() => setSexe('M')}
                          className="sr-only"
                        />
                        <span>Masculin</span>
                      </label>
                      <label
                        className={`flex-1 flex items-center justify-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                          sexe === 'F'
                            ? 'border-purple-500 bg-purple-50 text-purple-700'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <input
                          type="radio"
                          value="F"
                          checked={sexe === 'F'}
                          onChange={() => setSexe('F')}
                          className="sr-only"
                        />
                        <span>Féminin</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date de naissance</label>
                    <input
                      type="date"
                      value={dateNaissance}
                      onChange={(e) => setDateNaissance(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      style={{ colorScheme: 'light' }}
                    />
                  </div>
                </div>

                {/* Pays d'origine / Handicap */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pays d'origine</label>
                    <FilterableDropdown
                      options={countries.map((c) => ({
                        value: c.code,
                        label: c.nameFr,
                      }))}
                      value={paysOrigine}
                      onChange={setPaysOrigine}
                      placeholder="Sélectionner un pays..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Situation de handicap</label>
                    <select
                      value={handicap}
                      onChange={(e) => setHandicap(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      {HANDICAP_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Section 2: Coordonnées */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <SectionHeader
              title="Section 2: Coordonnées"
              icon={<Phone size={18} className="text-purple-600" />}
              expanded={expandedSections.has('coordinates')}
              onToggle={() => toggleSection('coordinates')}
              required
            />

            {expandedSections.has('coordinates') && (
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      id="staff-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="email@institution.ma"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Numéro de téléphone <span className="text-red-500">*</span>
                  </label>
                  <div className="flex">
                    <PhoneCountrySelector value={phoneCountry} onChange={setPhoneCountry} />
                    <input
                      id="staff-phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="flex-1 px-4 py-2 border border-l-0 border-gray-300 rounded-r-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="6XX XXX XXX"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adresse
                  </label>
                  <textarea
                    value={adresse}
                    onChange={(e) => setAdresse(e.target.value)}
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="Adresse complète (optionnel)"
                  />
                </div>

              </div>
            )}
          </div>

          {/* Section 3: Contact d'Urgence */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <SectionHeader
              title="Section 3: Contact d'Urgence"
              icon={<AlertTriangle size={18} className="text-amber-500" />}
              expanded={expandedSections.has('emergency')}
              onToggle={() => toggleSection('emergency')}
              optional
            />

            {expandedSections.has('emergency') && (
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom du contact
                    </label>
                    <input
                      type="text"
                      value={urgenceNom}
                      onChange={(e) => setUrgenceNom(capitalizeFirstLetter(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="Nom complet"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Relation
                    </label>
                    <select
                      value={urgenceRelation}
                      onChange={(e) => setUrgenceRelation(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      {emergencyRelationSelectOptions().map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Numéro de téléphone
                  </label>
                  <div className="flex">
                    <PhoneCountrySelector value={urgencePhoneCountry} onChange={setUrgencePhoneCountry} />
                    <input
                      type="tel"
                      value={urgenceNumero}
                      onChange={(e) => setUrgenceNumero(e.target.value)}
                      className="flex-1 px-4 py-2 border border-l-0 border-gray-300 rounded-r-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="6XX XXX XXX"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adresse du contact
                  </label>
                  <input
                    type="text"
                    value={urgenceAdresse}
                    onChange={(e) => setUrgenceAdresse(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="Adresse complète"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Section 4: Informations Professionnelles (for ALL staff) */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <SectionHeader
              title="Section 4: Informations Professionnelles"
              icon={<Briefcase size={18} className="text-purple-600" />}
              expanded={expandedSections.has('professional')}
              onToggle={() => toggleSection('professional')}
              required
            />

            {expandedSections.has('professional') && (
              <div className="p-6 space-y-4">
                {/* Fonction - Required */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fonction <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="staff-fonction"
                    type="text"
                    value={fonction}
                    onChange={(e) => setFonction(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="Ex: Directeur Pédagogique, Professeur, etc."
                    required
                  />
                </div>

                {/* Statut Professeur - Only for TEACHER role, shown BEFORE salary */}
                {isTeacherRole && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Statut <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-4">
                      <label
                        className={`flex-1 flex items-center justify-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                          statutProfesseur === 'P'
                            ? 'border-purple-500 bg-purple-50 text-purple-700'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <input
                          type="radio"
                          value="P"
                          checked={statutProfesseur === 'P'}
                          onChange={() => setStatutProfesseur('P')}
                          className="sr-only"
                        />
                        <span>Permanent</span>
                      </label>
                      <label
                        className={`flex-1 flex items-center justify-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                          statutProfesseur === 'V'
                            ? 'border-amber-500 bg-amber-50 text-amber-700'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <input
                          type="radio"
                          value="V"
                          checked={statutProfesseur === 'V'}
                          onChange={() => setStatutProfesseur('V')}
                          className="sr-only"
                        />
                        <span>Vacataire</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Date de recrutement - Required */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Calendar className="inline w-4 h-4 mr-1" />
                    Date de recrutement <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="staff-dateRecrutement"
                    type="date"
                    value={dateRecrutement}
                    onChange={(e) => setDateRecrutement(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    style={{ colorScheme: 'light' }}
                    required
                  />
                </div>

                {/* Salaire Mensuel Net - For non-TEACHER or Permanent TEACHER */}
                {(!isTeacherRole || (isTeacherRole && statutProfesseur === 'P')) && !isVacataire && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <DollarSign className="inline w-4 h-4 mr-1" />
                      Salaire Mensuel Net <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        id="staff-salaireMensuelNet"
                        type="text"
                        value={salaireMensuelNet}
                        onChange={(e) => {
                          const numericValue = e.target.value.replace(/\D/g, '');
                          if (numericValue) {
                            const number = parseInt(numericValue, 10);
                            setSalaireMensuelNet(number.toLocaleString('fr-MA'));
                          } else {
                            setSalaireMensuelNet('');
                          }
                        }}
                        className="w-full px-4 py-2 pr-16 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="15 000"
                        required
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-500">
                        MAD
                      </span>
                    </div>
                  </div>
                )}

                {/* Taux Horaire - For TEACHER Vacataire */}
                {isTeacherRole && statutProfesseur === 'V' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <DollarSign className="inline w-4 h-4 mr-1" />
                      Taux Horaire <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        id="staff-tauxHoraire"
                        type="text"
                        value={tauxHoraire}
                        onChange={(e) => {
                          const numericValue = e.target.value.replace(/\D/g, '');
                          setTauxHoraire(numericValue);
                        }}
                        className="w-full px-4 py-2 pr-16 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="200"
                        required
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-500">
                        MAD/h
                      </span>
                    </div>
                  </div>
                )}

                {/* Info for non-TEACHER vacataire */}
                {!isTeacherRole && isVacataire && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      <DollarSign className="inline w-4 h-4 mr-1" />
                      Le salaire mensuel net est automatiquement fixé à 0 MAD pour les vacataires.
                    </p>
                  </div>
                )}

                {/* Diplôme - Searchable Dropdown - Optional */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <GraduationCap className="inline w-4 h-4 mr-1" />
                    Diplôme (selon nomenclature E-DEP)
                  </label>
                  <div className="relative" ref={diplomeDropdownRef}>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        ref={diplomeInputRef}
                        type="text"
                        value={showDiplomeDropdown ? diplomeSearch : (diplomes.find(d => d.id === diplomeId)?.labelFr || diplomesList.find(d => d.id === diplomeId)?.label || '')}
                        onChange={(e) => {
                          setDiplomeSearch(e.target.value);
                          setShowDiplomeDropdown(true);
                          updateDropdownPosition(diplomeInputRef, setDiplomeDropdownPos);
                        }}
                        onFocus={() => {
                          setShowDiplomeDropdown(true);
                          setDiplomeSearch('');
                          updateDropdownPosition(diplomeInputRef, setDiplomeDropdownPos);
                        }}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="Rechercher un diplôme... (optionnel)"
                      />
                    </div>
                    {showDiplomeDropdown && ReactDOM.createPortal(
                      <div
                        data-dropdown-portal
                        className="fixed bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                        style={{
                          top: diplomeDropdownPos.top,
                          left: diplomeDropdownPos.left,
                          width: diplomeDropdownPos.width,
                          zIndex: 9999,
                        }}
                      >
                        {diplomesList
                          .filter(d => d.label.toLowerCase().includes(diplomeSearch.toLowerCase()))
                          .map(diplome => (
                            <button
                              key={diplome.id}
                              type="button"
                              onClick={() => {
                                setDiplomeId(diplome.id);
                                setDiplomeSearch('');
                                setShowDiplomeDropdown(false);
                              }}
                              className={`w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center justify-between ${
                                diplomeId === diplome.id ? 'bg-purple-50 text-purple-700' : ''
                              }`}
                            >
                              <span className="text-sm">{diplome.label}</span>
                              {diplomeId === diplome.id && (
                                <Check className="w-4 h-4 text-purple-600" />
                              )}
                            </button>
                          ))}
                        {diplomesList.filter(d => d.label.toLowerCase().includes(diplomeSearch.toLowerCase())).length === 0 && (
                          <div className="p-3 text-sm text-gray-500 text-center">
                            Aucun diplôme trouvé
                          </div>
                        )}
                      </div>,
                      document.body
                    )}
                  </div>
                </div>

                {/* RIB - Optional */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <CreditCard className="inline w-4 h-4 mr-1" />
                    RIB Bancaire
                  </label>
                  <input
                    id="staff-rib"
                    type="text"
                    value={rib}
                    onChange={(e) => {
                      const cleanValue = e.target.value.replace(/\D/g, '').slice(0, 24);
                      const groups = cleanValue.match(/.{1,4}/g);
                      setRib(groups ? groups.join(' ') : cleanValue);
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 font-mono"
                    placeholder="XXXX XXXX XXXX XXXX XXXX XXXX (optionnel)"
                    maxLength={29}
                  />
                  <p className="text-xs text-gray-500 mt-1">24 chiffres (format marocain)</p>
                </div>
              </div>
            )}
          </div>

          {/* Section 5: Activité d'enseignement - For staff who can teach (not TEACHER) */}
          {canAlsoBeProfesor && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <SectionHeader
                title="Section 5: Activité d'Enseignement"
                icon={<GraduationCap size={18} className="text-purple-600" />}
                expanded={expandedSections.has('alsoProf')}
                onToggle={() => toggleSection('alsoProf')}
                required
              />

              {expandedSections.has('alsoProf') && (
                <div className="p-6 space-y-4">
                  <div id="staff-isAlsoProfessor" className="space-y-2">
                    <span className="block text-sm font-medium text-gray-700">
                      Êtes-vous également professeur/enseignant ?<span className="text-red-500 ml-1">*</span>
                    </span>
                    <p className="text-xs text-gray-500">
                      Si vous enseignez en plus de votre fonction principale, veuillez répondre Oui pour compléter les informations professeur.
                    </p>
                    <div className="flex gap-4">
                      <label
                        className={`flex-1 flex items-center justify-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                          isAlsoProfessor === true
                            ? 'border-green-500 bg-green-50 text-green-700'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <input
                          type="radio"
                          checked={isAlsoProfessor === true}
                          onChange={() => {
                            setIsAlsoProfessor(true);
                            // Auto-set status to Permanent for staff who also teach
                            setStatutProfesseur('P');
                          }}
                          className="sr-only"
                        />
                        <span>Oui</span>
                      </label>
                      <label
                        className={`flex-1 flex items-center justify-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                          isAlsoProfessor === false
                            ? 'border-gray-500 bg-gray-50 text-gray-700'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <input
                          type="radio"
                          checked={isAlsoProfessor === false}
                          onChange={() => setIsAlsoProfessor(false)}
                          className="sr-only"
                        />
                        <span>Non</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Section 6: Informations E-DEP Professeur - For TEACHER role OR staff who also teach */}
          {showProfessorFields && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <SectionHeader
                title={isTeacherRole ? "Section 5: Informations E-DEP Professeur" : "Section 6: Informations E-DEP Professeur"}
                icon={<Award size={18} className="text-purple-600" />}
                expanded={expandedSections.has('professorEdep')}
                onToggle={() => toggleSection('professorEdep')}
                required={!isTeacherRole && isAlsoProfessor === true}
                optional={isTeacherRole}
              />

              {expandedSections.has('professorEdep') && (
                <div className="p-6 space-y-4">
                  {/* Info banner */}
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex gap-2">
                    <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-700">
                      <p className="font-medium">Informations E-DEP</p>
                      <p>Ces informations sont requises pour la déclaration E-DEP au ministère de l'enseignement supérieur.</p>
                    </div>
                  </div>

                  {/* Statut - For staff who also teach (TEACHER has it in Section 4) */}
                  {!isTeacherRole && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                      <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-purple-700">
                        <span className="font-medium">Permanent</span>
                        <span className="text-xs text-purple-500 ml-2">(automatique pour les responsables qui enseignent)</span>
                      </div>
                    </div>
                  )}

                  {/* Département d'attache - Searchable Dropdown - Required */}
                  <div id="staff-departementAttacheId">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Building2 className="inline w-4 h-4 mr-1" />
                      Département d'attache <span className="text-red-500">*</span>
                    </label>
                    <div className="relative" ref={deptDropdownRef}>
                      <input
                        ref={deptInputRef}
                        type="text"
                        value={showDeptDropdown ? deptSearch : (departements.find(d => d.id === departementAttacheId)?.labelFr || '')}
                        onChange={(e) => {
                          setDeptSearch(e.target.value);
                          setShowDeptDropdown(true);
                          updateDropdownPosition(deptInputRef, setDeptDropdownPos);
                        }}
                        onFocus={() => {
                          setShowDeptDropdown(true);
                          setDeptSearch('');
                          updateDropdownPosition(deptInputRef, setDeptDropdownPos);
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="Rechercher un département..."
                        required
                      />
                      {showDeptDropdown && ReactDOM.createPortal(
                        <div
                          data-dropdown-portal
                          className="fixed bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                          style={{
                            top: deptDropdownPos.top,
                            left: deptDropdownPos.left,
                            width: deptDropdownPos.width,
                            zIndex: 9999,
                          }}
                        >
                          {departements
                            .filter(d => d.labelFr.toLowerCase().includes(deptSearch.toLowerCase()))
                            .map(dept => (
                              <button
                                key={dept.id}
                                type="button"
                                onClick={() => {
                                  setDepartementAttacheId(dept.id);
                                  setDeptSearch('');
                                  setShowDeptDropdown(false);
                                }}
                                className={`w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center justify-between ${
                                  departementAttacheId === dept.id ? 'bg-purple-50 text-purple-700' : ''
                                }`}
                              >
                                <span className="text-sm">{dept.labelFr}</span>
                                {departementAttacheId === dept.id && (
                                  <Check className="w-4 h-4 text-purple-600" />
                                )}
                              </button>
                            ))}
                          {departements.filter(d => d.labelFr.toLowerCase().includes(deptSearch.toLowerCase())).length === 0 && (
                            <div className="p-3 text-sm text-gray-500 text-center">
                              {departements.length === 0 ? 'Aucun département configuré' : 'Aucun département trouvé'}
                            </div>
                          )}
                        </div>,
                        document.body
                      )}
                    </div>
                  </div>

                  {/* Grade - Searchable Dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
                    <div className="relative" ref={gradeDropdownRef}>
                      <input
                        ref={gradeInputRef}
                        type="text"
                        value={showGradeDropdown ? gradeSearch : (grades.find(g => g.id === gradeId)?.labelFr || '')}
                        onChange={(e) => {
                          setGradeSearch(e.target.value);
                          setShowGradeDropdown(true);
                          updateDropdownPosition(gradeInputRef, setGradeDropdownPos);
                        }}
                        onFocus={() => {
                          setShowGradeDropdown(true);
                          setGradeSearch('');
                          updateDropdownPosition(gradeInputRef, setGradeDropdownPos);
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="Rechercher un grade..."
                      />
                      {showGradeDropdown && ReactDOM.createPortal(
                        <div
                          data-dropdown-portal
                          className="fixed bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                          style={{
                            top: gradeDropdownPos.top,
                            left: gradeDropdownPos.left,
                            width: gradeDropdownPos.width,
                            zIndex: 9999,
                          }}
                        >
                          {grades
                            .filter(g => g.labelFr.toLowerCase().includes(gradeSearch.toLowerCase()))
                            .map(grade => (
                              <button
                                key={grade.id}
                                type="button"
                                onClick={() => {
                                  setGradeId(grade.id);
                                  setGradeSearch('');
                                  setShowGradeDropdown(false);
                                }}
                                className={`w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center justify-between ${
                                  gradeId === grade.id ? 'bg-purple-50 text-purple-700' : ''
                                }`}
                              >
                                <span className="text-sm">{grade.labelFr}</span>
                                {gradeId === grade.id && (
                                  <Check className="w-4 h-4 text-purple-600" />
                                )}
                              </button>
                            ))}
                          {grades.filter(g => g.labelFr.toLowerCase().includes(gradeSearch.toLowerCase())).length === 0 && (
                            <div className="p-3 text-sm text-gray-500 text-center">
                              {grades.length === 0 ? 'Aucun grade configuré' : 'Aucun grade trouvé'}
                            </div>
                          )}
                        </div>,
                        document.body
                      )}
                    </div>
                  </div>

                  {/* Diplôme & Spécialité */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Diplôme</label>
                      <FilterableDropdown
                        options={diplomesList.map((d) => ({
                          value: d.id,
                          label: d.label,
                        }))}
                        value={diplomeId}
                        onChange={setDiplomeId}
                        placeholder="Sélectionner un diplôme..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Spécialité</label>
                      <input
                        type="text"
                        value={specialite}
                        onChange={(e) => setSpecialite(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="Informatique, Mathématiques..."
                      />
                    </div>
                  </div>

                  {/* Vacataire-specific fields */}
                  {statutProfesseur === 'V' && (
                    <>
                      {/* Est originaire d'un établissement public */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Originaire d'un établissement public d'enseignement supérieur?
                        </label>
                        <div className="flex gap-4">
                          <label
                            className={`flex-1 flex items-center justify-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                              estOriginaireEtablissementPublic === 'O'
                                ? 'border-green-500 bg-green-50 text-green-700'
                                : 'border-gray-300 hover:border-gray-400'
                            }`}
                          >
                            <input
                              type="radio"
                              value="O"
                              checked={estOriginaireEtablissementPublic === 'O'}
                              onChange={() => setEstOriginaireEtablissementPublic('O')}
                              className="sr-only"
                            />
                            <span>Oui</span>
                          </label>
                          <label
                            className={`flex-1 flex items-center justify-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                              estOriginaireEtablissementPublic === 'N'
                                ? 'border-red-500 bg-red-50 text-red-700'
                                : 'border-gray-300 hover:border-gray-400'
                            }`}
                          >
                            <input
                              type="radio"
                              value="N"
                              checked={estOriginaireEtablissementPublic === 'N'}
                              onChange={() => setEstOriginaireEtablissementPublic('N')}
                              className="sr-only"
                            />
                            <span>Non</span>
                          </label>
                        </div>
                      </div>

                      {/* Établissement d'origine */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Établissement/Organisme d'origine
                        </label>
                        <input
                          type="text"
                          value={etablissementOrigine}
                          onChange={(e) => setEtablissementOrigine(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          placeholder="Ex: Université Mohammed V, Entreprise XYZ, etc."
                        />
                      </div>
                    </>
                  )}

                </div>
              )}
            </div>
          )}

          {/* Affectation Campus - ALWAYS show for all staff roles */}
          {/* Shows campus assignment and payroll campus for all staff */}
          {availableCampuses.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <SectionHeader
                title={`${isTeacherRole ? 'Section 6' : (showProfessorFields ? 'Section 7' : 'Section 6')}: Affectation Campus`}
                icon={<Building2 size={18} className="text-purple-600" />}
                expanded={expandedSections.has('campus')}
                onToggle={() => toggleSection('campus')}
                required={selectedRole !== 'DIRECTOR_GENERAL'}
              />

              {expandedSections.has('campus') && (
                <div className="p-6 space-y-4">
                  {/* DG gets all campuses automatically */}
                  {selectedRole === 'DIRECTOR_GENERAL' ? (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-green-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-green-800">Accès global</p>
                          <p className="text-sm text-green-700">
                            Le Directeur Général sera automatiquement affecté à tous les campus de l'établissement
                            ({availableCampuses.length} campus).
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Campus d'affectation */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Campus d'affectation <span className="text-red-500">*</span>
                        </label>
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-3">
                          <p className="text-sm text-blue-800">
                            {availableCampuses.length === 1
                              ? 'Ce membre sera affecté au campus unique de l\'établissement.'
                              : isMultiCampusRole
                                ? 'Ce rôle peut être affecté à un ou plusieurs campus. Sélectionnez tous les campus concernés.'
                                : 'Ce rôle ne peut être affecté qu\'à un seul campus.'}
                          </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {availableCampuses.map((campus) => (
                            <label
                              key={campus.id}
                              className={`flex items-center gap-3 p-3 border rounded-lg transition-colors ${
                                availableCampuses.length === 1
                                  ? 'border-purple-500 bg-purple-50 cursor-default'
                                  : selectedCampusIds.includes(campus.id)
                                    ? 'border-purple-500 bg-purple-50 cursor-pointer'
                                    : 'border-gray-300 hover:border-gray-400 cursor-pointer'
                              }`}
                            >
                              <input
                                type={isMultiCampusRole ? 'checkbox' : 'radio'}
                                name="campus"
                                checked={selectedCampusIds.includes(campus.id)}
                                onChange={() => toggleCampusSelection(campus.id)}
                                disabled={availableCampuses.length === 1}
                                className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                              />
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{campus.name}</div>
                                <div className="text-sm text-gray-500">{campus.city}</div>
                              </div>
                              {campus.isMainCampus && (
                                <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded">Principal</span>
                              )}
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Campus de Paie - always show for non-DG roles */}
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Campus de Paie <span className="text-red-500">*</span>
                        </label>
                        <p className="text-xs text-gray-500 mb-2">
                          Sélectionnez le campus responsable du paiement de ce membre du personnel.
                        </p>
                        {availableCampuses.length === 1 ? (
                          <div className="px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700">
                            {availableCampuses[0].name}
                          </div>
                        ) : (
                          <select
                            value={payrollCampusId}
                            onChange={(e) => setPayrollCampusId(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            required
                          >
                            <option value="">-- Sélectionner le campus de paie --</option>
                            {availableCampuses.map((campus) => (
                              <option key={campus.id} value={campus.id}>
                                {campus.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Section Documents */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <SectionHeader
              title="Documents à Attacher"
              icon={<FileText size={18} className="text-purple-600" />}
              expanded={expandedSections.has('documents')}
              onToggle={() => toggleSection('documents')}
              optional
            />

            {expandedSections.has('documents') && (
              <div className="p-6 space-y-4">
                {/* Info banner */}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex gap-2">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium">Documents facultatifs</p>
                    <p>Vous pouvez attacher des documents au profil du membre. Ces documents seront liés à son compte et accessibles depuis son profil.</p>
                    <p className="mt-1 text-xs">Formats acceptés: JPG, PNG, PDF, WEBP (max 10 Mo)</p>
                  </div>
                </div>

                {/* Document categories */}
                <div className="space-y-6">
                  {/* Identity Documents */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Documents d'identité
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {STAFF_DOCUMENT_TYPES.filter(d => d.category === 'identity').map((docType) => {
                        const uploadedDoc = documents.find(d => d.type === docType.type);
                        return (
                          <div
                            key={docType.type}
                            className={`relative border rounded-lg p-3 transition-colors ${
                              uploadedDoc
                                ? 'border-green-300 bg-green-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {uploadedDoc ? (
                                  <Check className="w-4 h-4 text-green-600" />
                                ) : (
                                  <File className="w-4 h-4 text-gray-400" />
                                )}
                                <span className="text-sm font-medium text-gray-700">{docType.label}</span>
                              </div>
                              {uploadedDoc ? (
                                <button
                                  type="button"
                                  onClick={() => handleDocumentRemove(docType.type)}
                                  className="p-1 text-red-500 hover:bg-red-50 rounded"
                                  title="Supprimer"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              ) : (
                                <label className="cursor-pointer p-1 text-purple-600 hover:bg-purple-50 rounded">
                                  <Upload className="w-4 h-4" />
                                  <input
                                    type="file"
                                    accept=".jpg,.jpeg,.png,.pdf,.webp"
                                    className="hidden"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) handleDocumentAdd(docType.type, file);
                                      e.target.value = '';
                                    }}
                                  />
                                </label>
                              )}
                            </div>
                            {uploadedDoc && (
                              <div className="mt-2 text-xs text-gray-500 truncate">
                                {uploadedDoc.file.name} ({formatFileSize(uploadedDoc.file.size)})
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Professional Documents */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <Briefcase className="w-4 h-4" />
                      Documents professionnels
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {STAFF_DOCUMENT_TYPES.filter(d => d.category === 'professional').map((docType) => {
                        const uploadedDoc = documents.find(d => d.type === docType.type);
                        return (
                          <div
                            key={docType.type}
                            className={`relative border rounded-lg p-3 transition-colors ${
                              uploadedDoc
                                ? 'border-green-300 bg-green-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {uploadedDoc ? (
                                  <Check className="w-4 h-4 text-green-600" />
                                ) : (
                                  <File className="w-4 h-4 text-gray-400" />
                                )}
                                <span className="text-sm font-medium text-gray-700">{docType.label}</span>
                              </div>
                              {uploadedDoc ? (
                                <button
                                  type="button"
                                  onClick={() => handleDocumentRemove(docType.type)}
                                  className="p-1 text-red-500 hover:bg-red-50 rounded"
                                  title="Supprimer"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              ) : (
                                <label className="cursor-pointer p-1 text-purple-600 hover:bg-purple-50 rounded">
                                  <Upload className="w-4 h-4" />
                                  <input
                                    type="file"
                                    accept=".jpg,.jpeg,.png,.pdf,.webp"
                                    className="hidden"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) handleDocumentAdd(docType.type, file);
                                      e.target.value = '';
                                    }}
                                  />
                                </label>
                              )}
                            </div>
                            {uploadedDoc && (
                              <div className="mt-2 text-xs text-gray-500 truncate">
                                {uploadedDoc.file.name} ({formatFileSize(uploadedDoc.file.size)})
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Professor Documents - Only show if teacher role or isAlsoProfessor */}
                  {(isTeacherRole || isAlsoProfessor) && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                        <GraduationCap className="w-4 h-4" />
                        Documents Professeur
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {STAFF_DOCUMENT_TYPES.filter(d => d.category === 'professor').map((docType) => {
                          // Skip vacataire-only documents if not vacataire
                          if (docType.vacataireOnly && statutProfesseur !== 'V') return null;
                          // Skip documents that require being from public institution if not from public institution
                          if (docType.fromPublicInstitutionOnly && estOriginaireEtablissementPublic !== 'O') return null;

                          const uploadedDoc = documents.find(d => d.type === docType.type);
                          return (
                            <div
                              key={docType.type}
                              className={`relative border rounded-lg p-3 transition-colors ${
                                uploadedDoc
                                  ? 'border-green-300 bg-green-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  {uploadedDoc ? (
                                    <Check className="w-4 h-4 text-green-600" />
                                  ) : (
                                    <File className="w-4 h-4 text-gray-400" />
                                  )}
                                  <span className="text-sm font-medium text-gray-700">{docType.label}</span>
                                </div>
                                {uploadedDoc ? (
                                  <button
                                    type="button"
                                    onClick={() => handleDocumentRemove(docType.type)}
                                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                                    title="Supprimer"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                ) : (
                                  <label className="cursor-pointer p-1 text-purple-600 hover:bg-purple-50 rounded">
                                    <Upload className="w-4 h-4" />
                                    <input
                                      type="file"
                                      accept=".jpg,.jpeg,.png,.pdf,.webp"
                                      className="hidden"
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) handleDocumentAdd(docType.type, file);
                                        e.target.value = '';
                                      }}
                                    />
                                  </label>
                                )}
                              </div>
                              {uploadedDoc && (
                                <div className="mt-2 text-xs text-gray-500 truncate">
                                  {uploadedDoc.file.name} ({formatFileSize(uploadedDoc.file.size)})
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                </div>

                {/* Summary of uploaded documents */}
                {documents.length > 0 && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 text-green-700">
                      <Check className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {documents.length} document{documents.length > 1 ? 's' : ''} prêt{documents.length > 1 ? 's' : ''} à être téléchargé{documents.length > 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Section Suivi des copies physiques */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <SectionHeader
              title="Suivi des copies physiques"
              icon={<ClipboardCheck size={18} className="text-emerald-600" />}
              expanded={expandedSections.has('physicalDocs')}
              onToggle={() => toggleSection('physicalDocs')}
              optional
            />

            {expandedSections.has('physicalDocs') && (
              <div className="p-6 space-y-4">
                {/* Info banner */}
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex gap-2">
                  <Info className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-emerald-700">
                    <p className="font-medium">Documents physiques</p>
                    <p>Cochez les documents physiques que vous avez reçus pour ce membre du personnel.</p>
                  </div>
                </div>

                {/* Physical document checkboxes */}
                <div className="space-y-3">
                  {/* CIN or Passport based on nationality */}
                  {paysOrigine === 'MA' || paysOrigine === '' ? (
                    <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={physicalDocCin}
                        onChange={(e) => setPhysicalDocCin(e.target.checked)}
                        className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                      />
                      <div>
                        <span className="font-medium text-gray-900">CIN</span>
                        <p className="text-xs text-gray-500">Carte d'identité nationale</p>
                      </div>
                    </label>
                  ) : (
                    <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={physicalDocPassport}
                        onChange={(e) => setPhysicalDocPassport(e.target.checked)}
                        className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                      />
                      <div>
                        <span className="font-medium text-gray-900">Passeport</span>
                        <p className="text-xs text-gray-500">Copie du passeport</p>
                      </div>
                    </label>
                  )}

                  {/* CV */}
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={physicalDocCv}
                      onChange={(e) => setPhysicalDocCv(e.target.checked)}
                      className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                    />
                    <div>
                      <span className="font-medium text-gray-900">CV</span>
                      <p className="text-xs text-gray-500">Curriculum Vitae</p>
                    </div>
                  </label>

                  {/* Contrat de travail */}
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={physicalDocContratTravail}
                      onChange={(e) => setPhysicalDocContratTravail(e.target.checked)}
                      className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                    />
                    <div>
                      <span className="font-medium text-gray-900">Contrat de travail</span>
                      <p className="text-xs text-gray-500">Contrat de travail signé</p>
                    </div>
                  </label>

                  {/* Diplôme */}
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={physicalDocDiplome}
                      onChange={(e) => setPhysicalDocDiplome(e.target.checked)}
                      className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                    />
                    <div>
                      <span className="font-medium text-gray-900">Diplôme</span>
                      <p className="text-xs text-gray-500">Copie du diplôme le plus élevé</p>
                    </div>
                  </label>

                  {/* Attestation RIB */}
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={physicalDocAttestationRib}
                      onChange={(e) => setPhysicalDocAttestationRib(e.target.checked)}
                      className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                    />
                    <div>
                      <span className="font-medium text-gray-900">Attestation RIB</span>
                      <p className="text-xs text-gray-500">Relevé d'identité bancaire</p>
                    </div>
                  </label>

                  {/* Reglement interieur */}
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={physicalDocReglementInterieur}
                      onChange={(e) => setPhysicalDocReglementInterieur(e.target.checked)}
                      className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                    />
                    <div>
                      <span className="font-medium text-gray-900">Reglement interieur</span>
                      <p className="text-xs text-gray-500">Reglement interieur signe</p>
                    </div>
                  </label>

                  {/* Consentement medias */}
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={physicalDocConsentement}
                      onChange={(e) => setPhysicalDocConsentement(e.target.checked)}
                      className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                    />
                    <div>
                      <span className="font-medium text-gray-900">Consentement medias</span>
                      <p className="text-xs text-gray-500">Consentement medias et reseaux sociaux</p>
                    </div>
                  </label>

                  {/* Engagement des professeurs - only for teachers or isAlsoProfessor */}
                  {showProfessorFields && (
                    <label className="flex items-center gap-3 p-3 border border-amber-200 bg-amber-50 rounded-lg hover:bg-amber-100 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={physicalDocEngagementProfesseur}
                        onChange={(e) => setPhysicalDocEngagementProfesseur(e.target.checked)}
                        className="w-4 h-4 text-amber-600 border-amber-300 rounded focus:ring-amber-500"
                      />
                      <div>
                        <span className="font-medium text-amber-900">Engagement des professeurs</span>
                        <p className="text-xs text-amber-700">Document d'engagement pour les activités d'enseignement</p>
                      </div>
                    </label>
                  )}
                </div>
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={loading}
          >
            Annuler
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={loading || success}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Création...</span>
              </>
            ) : success ? (
              <>
                <Check size={18} />
                <span>Créé !</span>
              </>
            ) : (
              <span>Créer le compte</span>
            )}
          </button>
        </div>
      </div>

      {/* Bulk Import Professors Modal */}
      <BulkImportProfessorsModal
        isOpen={showBulkImportProfessorsModal}
        onClose={() => setShowBulkImportProfessorsModal(false)}
        onSuccess={() => {
          setShowBulkImportProfessorsModal(false);
          onSuccess(); // Refresh the parent list
        }}
      />

      {/* Bulk Import Staff Modal */}
      <BulkImportStaffModal
        isOpen={showBulkImportStaffModal}
        onClose={() => setShowBulkImportStaffModal(false)}
        onSuccess={() => {
          setShowBulkImportStaffModal(false);
          onSuccess(); // Refresh the parent list
        }}
      />
    </div>
  );
};

export default CreateStaffModal;
